import { Carousel } from 'bootstrap';
import { disposeBootstrapCarouselSafe } from '@/js/utils/dispose-bootstrap-carousel';
interface BreakpointConfig {
  desktopXl: number;
  desktop: number;
  tablet: number;
  middle: number;
  mobile: number;
  mobileSm?: number;
}

interface CarouselOptions {
  rootId?: string;
  sourceId?: string;
  root?: HTMLElement | null;
  source?: HTMLElement | null;
  breakpoints: BreakpointConfig;
  indicators?: boolean; // по умолчанию true
  fillIncompleteSlide?: boolean;
  paddedSlideClass?: string;
  onRender?: () => void;
}

export class ResponsiveCarouselWidget {
  private root: HTMLElement | null;
  private source: HTMLElement | null;
  private nativeNavigation: HTMLElement | null;
  private indicatorsWrap: HTMLElement | null;
  private items: HTMLElement[] = [];
  private observer: MutationObserver | null = null;
  private resizeTimeout: number | null = null;
  private breakpoints: BreakpointConfig;
  private indicatorsEnabled: boolean;
  private fillIncompleteSlide: boolean;
  private paddedSlideClass: string | undefined;
  private onRender?: () => void;
  private needButtons: boolean = true;
  private onResize: (() => void) | null = null;
  private onSlide: ((event: Event) => void) | null = null;

  constructor(options: CarouselOptions) {
    this.root =
      options.root ??
      (options.rootId ? document.getElementById(options.rootId) : null);
    this.source =
      options.source ??
      (options.sourceId ? document.getElementById(options.sourceId) : null);
    this.breakpoints = options.breakpoints;
    this.indicatorsEnabled = options.indicators ?? true;
    this.fillIncompleteSlide = options.fillIncompleteSlide ?? false;
    this.paddedSlideClass = options.paddedSlideClass;
    this.nativeNavigation = this.root?.querySelector('.carousel-controls') ?? null;
    this.indicatorsWrap =
      this.nativeNavigation?.querySelector('.carousel-indicators-custom') ?? null;
    this.onRender = options?.onRender;
  }

  public init() {
    if (!this.source?.children || this.source?.children?.length === 0 || !this.root) {
      return;
    }
    this.collectItems();
    this.initObserver();
    this.initResizeListener();
    this.render(); // первая отрисовка
    this.bindEvents();
  }

  public destroy(): void {
    this.observer?.disconnect();
    this.observer = null;
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
      this.onResize = null;
    }
    if (this.resizeTimeout) {
      window.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
    if (this.root && this.onSlide) {
      this.root.removeEventListener('slide.bs.carousel', this.onSlide);
      this.onSlide = null;
    }
    const root = this.root;
    if (!root) {
      this.items = [];
      return;
    }
    disposeBootstrapCarouselSafe(root, () => {
      this.clearCarousel();
      this.items = [];
    });
  }

  //
  // Чтение карточек из source
  //
  private collectItems() {
    this.items = this.source?.children ? (Array.from(this.source.children) as HTMLElement[]) : [];
  }

  //
  // Определяем количество элементов в слайде
  //
  private getCardsPerSlide(): number {
    const width = window.innerWidth;

    if (width >= 1600) return this.breakpoints.desktopXl;
    if (width >= 1200) return this.breakpoints.desktop;
    if (width >= 992) return this.breakpoints.tablet;
    if (width >= 768) return this.breakpoints.middle;
    if (width > 576) return this.breakpoints.mobile;
    return this.breakpoints.mobileSm ?? this.breakpoints.mobile;
  }

  //
  // Определяем количество элементов в слайде
  //
  private switchNeedButtons(chunksNumber: number): void {
    if (chunksNumber <= 1) {
      this.needButtons = false;
    } else {
      this.needButtons = Boolean(this.indicatorsEnabled);
    }
  }

  //
  // Следим за изменениями Bitrix — подгрузка по AJAX
  //
  private initObserver() {
    if (!this.source) return;

    this.observer = new MutationObserver(() => {
      this.collectItems();
      this.render();
    });

    this.observer.observe(this.source, {
      childList: true,
      subtree: true,
    });
  }

  //
  // Debounce resize listener
  //
  private initResizeListener() {
    this.onResize = () => {
      if (this.resizeTimeout) {
        window.clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = window.setTimeout(() => {
        this.render();
      }, 200);
    };
    window.addEventListener('resize', this.onResize);
  }

  //
  // Очистка карусели (только слайды; разметка .carousel-controls из шаблона сохраняется)
  //
  private clearCarousel() {
    if (!this.root) return;

    const inner = this.root.querySelector('.carousel-inner');
    if (inner) {
      inner.innerHTML = '';
      return;
    }

    this.root.innerHTML = '<div class="carousel-inner"></div>';
  }

  //
  // Подготовка блоков навигации
  //
  private prepareNavigation() {
    if (!this.root) return;

    if (this.indicatorsWrap && this.nativeNavigation && this.needButtons) {
      this.nativeNavigation.classList.remove('d-none');
      const inner = this.root.querySelector('.carousel-inner');
      if (inner) {
        this.root.insertBefore(this.nativeNavigation, inner);
      }
    } else if (this.indicatorsWrap && this.nativeNavigation && !this.needButtons) {
      this.nativeNavigation.classList.add('d-none');
    } else if (this.needButtons) {
      const indicatorsWrap = document.createElement('div');
      indicatorsWrap.className = 'carousel-indicators-custom';
      this.root.appendChild(indicatorsWrap);
    }
  }

  //
  // Основная логика рендера
  //
  private render() {
    if (!this.items.length || !this.root) return;

    this.clearCarousel();

    const perSlide = this.getCardsPerSlide();
    const inner = this.root.querySelector('.carousel-inner');
    const renderItems = this.getItemsForRender(perSlide);
    const chunks = this.chunkArray(renderItems, perSlide);

    this.switchNeedButtons(chunks?.length);
    this.prepareNavigation();

    if (!inner) return;
    //
    // РЕНДЕР СЛАЙДОВ
    //
    chunks.forEach((group, index) => {
      const slide = document.createElement('div');
      const slideInner = document.createElement('div');
      slide.className = 'carousel-item' + (index === 0 ? ' active' : '');
      slideInner.className = 'carousel-item-inner';

      group.forEach(({ element, isPadded }) => {
        const clone = element.cloneNode(true) as HTMLElement;
        clone.removeAttribute('data-resident-bound');
        delete clone.dataset.residentBound;
        if (isPadded && this.paddedSlideClass) {
          clone.classList.add(this.paddedSlideClass);
          clone.setAttribute('aria-hidden', 'true');
        }
        slideInner.appendChild(clone);
      });

      slide.appendChild(slideInner);
      inner.appendChild(slide);
    });

    //
    // Обновить/создать Bootstrap карусель
    //
    const carousel = Carousel.getOrCreateInstance(this.root, { ride: false, pause: false });
    carousel.to(0);
    if (this.onRender) {
      this.onRender();
    }
  }

  //
  // меняет состояние индикатора
  //
  private setActiveIndicator(activeIndex: number): void {
    if (!this.root) return;

    const indicators = this.root.querySelectorAll('.carousel-indicators-custom button');
    if (!indicators) return;

    indicators.forEach((indicator, index) => {
      if (index === activeIndex) {
        indicator.classList.add('active');
      } else {
        indicator.classList.remove('active');
      }
    });
  }

  private bindEvents(): void {
    if (this.root) {
      this.onSlide = (event: Event) => {
        const bsEvent = event as unknown as { to: number };
        this.setActiveIndicator(bsEvent.to);
      };
      this.root.addEventListener('slide.bs.carousel', this.onSlide);
    }

    this.setActiveIndicator(0);
  }

  //
  // Хелпер для разбивки массива
  //
  private chunkArray<T>(arr: T[], size: number): T[][] {
    const res: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      res.push(arr.slice(i, i + size));
    }
    return res;
  }

  private getItemsForRender(perSlide: number) {
    const items = this.items.map((element) => ({ element, isPadded: false }));
    if (!this.fillIncompleteSlide || !this.items.length) return items;

    const rest = this.items.length % perSlide;
    if (!rest) return items;

    const need = perSlide - rest;
    for (let i = 0; i < need; i++) {
      items.push({ element: this.items[i % this.items.length], isPadded: true });
    }

    return items;
  }
}
